import { h } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import { useForm, Controller } from 'react-hook-form';
import Autocomplete from 'react-google-autocomplete';
import Input from '../../components/form/Input';
import Checkbox from '../../components/form/Checkbox';
import FormStepLayout from '../../templates/FormStepLayout/FormStepLayout';
import RightSidebar from '../../components/sidebars/RightSidebar';
import useFormStore from '../../store/useFormStore';
import AddressValidationModal from '../../components/AddressValidationModal';
import Alert from '../../components/Alert';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormFieldErrorMessage,
  FormDescription
} from '../../components/form/Form';

export default function ServiceInformation({ onNext, onBack, onReset, country = 'US', userPreFilledData = {} }) {
  // Get data from Zustand store
  const userFormInformation = useFormStore((state) => state.userFormInformation);
  const setUserFormData = useFormStore((state) => state.setUserFormData);
  const currentPostalCode = useFormStore((state) => state.currentPostalCode);
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const needsPortableEquipment = useFormStore((state) => state.needsPortableEquipment);
  const togglePortableEquipment = useFormStore((state) => state.togglePortableEquipment);
  const errorMessageElement = useRef(null);

  // Get Google API key from environment
  const googleApiKey = drupalSettings.coit.api_key;

  // Get country code for restricting autocomplete results
  const countryCode = currentFranchise?.address?.country_code || country;

  // Address validation state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [currentAddressData, setCurrentAddressData] = useState(null);
  const [recommendedAddressData, setRecommendedAddressData] = useState(null);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const {
    register,
    handleSubmit: handleFormSubmit,
    control,
    setValue,
    watch,
    formState: { errors, touchedFields, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: userFormInformation.firstName || userPreFilledData.first_name || '',
      lastName: userFormInformation.lastName || userPreFilledData.last_name || '',
      streetAddress: userFormInformation.streetAddress || '',
      unit: userFormInformation.unit || '',
      city: userFormInformation.city || '',
      state: userFormInformation.state || '',
      postalCode: userFormInformation.postalCode || currentPostalCode || '',
      email: userFormInformation.email || userPreFilledData.email || '',
      phoneNumber: userFormInformation.phoneNumber || '',
      signUpForEmails: userFormInformation.signUpForEmails || false,
      signUpForTexts: userFormInformation.signUpForTexts || false
    }
  });

  // Populate form with stored data when component mounts
  useEffect(() => {
    // Populate saved form data from store
    if (userFormInformation && Object.keys(userFormInformation).length > 0) {
      Object.keys(userFormInformation).forEach(key => {
        const value = userFormInformation[key];
        // Set value even if it's an empty string or false (for checkboxes)
        if (value !== undefined && value !== null) {
          setValue(key, value, { shouldValidate: true });
        }
      });
    }

    // Prefill from userPreFilledData.
    if (userPreFilledData) {
      if (userPreFilledData.first_name && !userFormInformation?.firstName) {
        setValue('firstName', userPreFilledData.first_name, { shouldValidate: true });
      }
      if (userPreFilledData.last_name && !userFormInformation?.lastName) {
        setValue('lastName', userPreFilledData.last_name, { shouldValidate: true });
      }
      if (userPreFilledData.email && !userFormInformation?.email) {
        setValue('email', userPreFilledData.email, { shouldValidate: true });
      }
    }

    // Use currentPostalCode as fallback if not already set from userFormInformation
    if (currentPostalCode && !userFormInformation?.postalCode) {
      setValue('postalCode', currentPostalCode, { shouldValidate: true });
    }
  }, [currentPostalCode, userFormInformation, userPreFilledData, setValue]);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '').replace(/^1/, '').slice(0, 10); // Drop country code, cap at 10 digits
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  // Validation rules
  const validationRules = {
    firstName: {
      required: 'First name is required'
    },
    lastName: {
      required: 'Last name is required'
    },
    streetAddress: {
      required: 'Street address is required'
    },
    city: {
      required: 'City is required'
    },
    state: {
      required: countryCode === 'CA' ? 'Province is required' : 'State is required'
    },
    postalCode: {
      required: countryCode === 'CA' ? 'Postal code is required' : 'ZIP code is required',
      pattern: countryCode === 'CA'
        ? {
            value: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
            message: 'Invalid postal code format (e.g., A1A 1A1)'
          }
        : {
            value: /^\d{5}(-\d{4})?$/,
            message: 'Invalid ZIP code format (e.g., 94107)'
          }
    },
    email: {
      required: 'Email is required',
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Invalid email address'
      }
    },
    phoneNumber: {
      required: 'Phone number is required',
      validate: (value) => {
        const digits = value.replace(/\D/g, '');
        return digits.length === 10 || 'Phone number must be 10 digits';
      }
    }
  };

  // Validate address with API
  const validateAddress = async (data) => {
    setIsValidatingAddress(true);
    setValidationError(null);
    try {
      // First, fetch the CSRF token
      const csrfResponse = await fetch('/session/token');
      const csrfToken = await csrfResponse.text();

      const addressPayload = {
        country_code: countryCode,
        address: data.streetAddress,
        address_2: data.unit || '',
        city: data.city,
        state_province: data.state,
        postal_code: data.postalCode
      };
      // console.log('Sending address validation request with payload:', addressPayload);

      const response = await fetch(`/api/location/${currentFranchise.id}/validate/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(addressPayload)
      });

      const result = await response.json();

      // console.log('Address validation result:', result);

      // Check if validation failed and show message
      if (!result.success) {
        setValidationError(result.message || 'Unable to validate address.');
        // Scroll to error message
        setTimeout(() => {
          errorMessageElement.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 0);
        return;
      }

      // Check if address is valid
      if (result.valid) {
        proceedWithFormData(data);
      } else {
        setCurrentAddressData(addressPayload);
        setRecommendedAddressData(result.address);
        setPendingFormData(data);
        setShowAddressModal(true);
      }


    } catch (error) {
      // console.error('Error validating address:', error);
      setValidationError('An error occurred while validating the address.');
      setTimeout(() => {
        errorMessageElement.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 0);
    } finally {
      setIsValidatingAddress(false);
    }
  };

  // Handle address selection from modal
  const handleAddressSelection = (selectedAddress) => {
    // Close modal
    setShowAddressModal(false);

    // Update form values with selected address
    setValue('streetAddress', selectedAddress.address, { shouldValidate: true });
    setValue('unit', selectedAddress.address_2 || '', { shouldValidate: true });
    setValue('city', selectedAddress.city, { shouldValidate: true });
    setValue('state', selectedAddress.state_province, { shouldValidate: true });
    setValue('postalCode', selectedAddress.postal_code, { shouldValidate: true });

    // Update the pending form data with selected address
    const updatedData = {
      ...pendingFormData,
      streetAddress: selectedAddress.address,
      unit: selectedAddress.address_2 || '',
      city: selectedAddress.city,
      state: selectedAddress.state_province,
      postalCode: selectedAddress.postal_code
    };
    proceedWithFormData(updatedData);
  };

  // Add opted-in visitors to the abandoned-cart list. They are removed
  // from the list once the order is completed.
  const subscribeToNewsletter = async (data) => {
    if (!data.signUpForEmails && !data.signUpForTexts) {
      return;
    }
    try {
      const csrfResponse = await fetch('/session/token');
      const csrfToken = await csrfResponse.text();
      await fetch(`/api/location/${currentFranchise.id}/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          postal_code: data.postalCode,
          phone: data.phoneNumber,
          sign_up_for_emails: data.signUpForEmails,
          sign_up_for_texts: data.signUpForTexts
        })
      });
    } catch (error) {
      // Ignore.
    }
  };

  // Proceed with form submission
  const proceedWithFormData = (data) => {
    // Save form data to Zustand store
    setUserFormData(data);

    subscribeToNewsletter(data);

    if (onNext) {
      onNext(data);
    }
  };

  // Handle form submission
  const onSubmit = (data) => {
    // Validate address if all fields are filled
    if (data.streetAddress && data.city && data.state && data.postalCode) {
      validateAddress(data);
    } else {
      proceedWithFormData(data);
    }
  };

  return (
    <FormStepLayout
      onBack={onBack}
      onNext={handleFormSubmit(onSubmit)}
      nextText={isValidatingAddress ? "Validating Address..." : "Next: Review Order"}
      nextDisabled={!isValid || isValidatingAddress}
      leftSidebar={true}
      rightSidebar={<RightSidebar showAppointmentDetails={true} />}
      title="Add Your Information"
    >

      {/* Validation Error Message */}
      {validationError && (
        <div ref={errorMessageElement} className="scroll-mt-48">
          <Alert
            type="error"
            title="Address Validation Error"
            onClose={() => setValidationError(null)}
            bordered
          >
            <div className="flex flex-col gap-1">
              <p className="mb-0">{validationError}</p>
              {onReset && (
                <div>
                  <a
                    type="button"
                    onClick={onReset}
                    className="text-coit-red hover:text-coit-red-darker underline font-medium"
                  >
                    Find Your Local COIT
                  </a>
                </div>
              )}
            </div>
          </Alert>
        </div>
      )}

      <Form onSubmit={handleFormSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField name="firstName">
            <FormItem>
              <FormLabel required>First Name</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  error={touchedFields.firstName && errors.firstName}
                  placeholder="Enter first name"
                  {...register('firstName', validationRules.firstName)}
                />
                <FormFieldErrorMessage error={touchedFields.firstName && errors.firstName?.message} />
              </FormControl>
            </FormItem>
          </FormField>

          <FormField name="lastName">
            <FormItem>
              <FormLabel required>Last Name</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  error={touchedFields.lastName && errors.lastName}
                  placeholder="Enter last name"
                  {...register('lastName', validationRules.lastName)}
                />
              </FormControl>
              <FormFieldErrorMessage error={touchedFields.lastName && errors.lastName?.message} />
            </FormItem>
          </FormField>
        </div>

        {/* Street Address with Google Autocomplete */}
        <FormField name="streetAddress">
          <FormItem>
            <FormLabel required>Street Address</FormLabel>
            <FormControl>
              <Controller
                name="streetAddress"
                control={control}
                rules={validationRules.streetAddress}
                render={({ field }) => (
                  // TODO: We want to add bounds based on franchise location later Maybe...
                  <Autocomplete
                    id="streetAddress"
                    name="streetAddress"
                    apiKey={googleApiKey}
                    aria-invalid={
                      touchedFields.streetAddress && errors.streetAddress
                        ? 'true'
                        : undefined
                    }
                    aria-describedby={
                      touchedFields.streetAddress && errors.streetAddress
                        ? 'streetAddress-error'
                        : undefined
                    }
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    onPlaceSelected={(place) => {
                      // Set the street address
                      const streetNumber = place.address_components?.find(c => c.types.includes('street_number'))?.long_name || '';
                      const route = place.address_components?.find(c => c.types.includes('route'))?.long_name || '';
                      const streetAddress = `${streetNumber} ${route}`.trim();
                      field.onChange(streetAddress);

                      // Auto-populate city
                      const city = place.address_components?.find(c => c.types.includes('locality'))?.long_name || '';
                      if (city) {
                        setValue('city', city, { shouldValidate: true });
                      }

                      // Auto-populate state
                      const state = place.address_components?.find(c => c.types.includes('administrative_area_level_1'))?.short_name || '';
                      if (state) {
                        setValue('state', state, { shouldValidate: true });
                      }

                      // Auto-populate postal code
                      const postalCode = place.address_components?.find(c => c.types.includes('postal_code'))?.long_name || '';
                      if (postalCode) {
                        setValue('postalCode', postalCode, { shouldValidate: true });
                      }
                    }}
                    options={{
                      types: ['address'],
                      componentRestrictions: { country: countryCode }
                    }}
                    className={`flex h-10 w-full mb-0 border bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
                      touchedFields.streetAddress && errors.streetAddress
                        ? 'border-coit-error focus-visible:ring-coit-error'
                        : 'border-gray-300 focus-visible:ring-black'
                    }`}
                    placeholder="Enter your street address"
                  />
                )}
              />
            </FormControl>
            <FormFieldErrorMessage error={touchedFields.streetAddress && errors.streetAddress?.message} />
          </FormItem>
        </FormField>

        {/* Unit/Apartment */}
        <FormField name="unit">
          <FormItem>
            <FormLabel>Unit / Apartment Number</FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder="Unit, Apt, Suite (optional)"
                {...register('unit')}
              />
            </FormControl>
            <FormFieldErrorMessage error={touchedFields.unit && errors.unit?.message} />
          </FormItem>
        </FormField>

        {/* City, State, ZIP */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField name="city">
            <FormItem>
              <FormLabel required>City / Town</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  error={touchedFields.city && errors.city}
                  placeholder="City"
                  {...register('city', validationRules.city)}
                />
              </FormControl>
              <FormFieldErrorMessage error={touchedFields.city && errors.city?.message} />
            </FormItem>
          </FormField>

          <FormField name="state">
            <FormItem>
              <FormLabel required>
                {countryCode === 'CA' ? 'Province' : 'State'}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  error={touchedFields.state && errors.state}
                  placeholder={countryCode === 'CA' ? 'Province' : 'State'}
                  maxLength={2}
                  {...register('state', validationRules.state)}
                />
              </FormControl>
              <FormFieldErrorMessage error={touchedFields.state && errors.state?.message} />
            </FormItem>
          </FormField>

          <FormField name="postalCode">
            <FormItem>
              <FormLabel required>
                {countryCode === 'CA' ? 'Postal Code' : 'ZIP Code'}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  error={touchedFields.postalCode && errors.postalCode}
                  placeholder={countryCode === 'CA' ? 'A1A 1A1' : '94107'}
                  maxLength={countryCode === 'CA' ? 7 : 10}
                  {...register('postalCode', validationRules.postalCode)}
                />
              </FormControl>
              <FormFieldErrorMessage error={touchedFields.postalCode && errors.postalCode?.message} />
            </FormItem>
          </FormField>
        </div>

        {/* Tell Us About Your Home - Portable Equipment */}
        <div className="">
          <div className="flex flex-col gap-3">

            {/* Checkbox for portable equipment */}
            <Checkbox
              checked={needsPortableEquipment}
              onChange={togglePortableEquipment}
              className="mt-2"
              label="I have no parking and/or am on a 3rd floor or higher"
            />
            <div className="text-sm mb-0">If we need to park across or down the street, or clean above the 2nd floor, portable equipment may be required and additional charges will apply.</div>
          </div>
        </div>

        {/* Email */}
        <FormField name="email">
          <FormItem>
            <FormLabel required>Email Address</FormLabel>
            <FormControl>
              <Input
                type="email"
                error={touchedFields.email && errors.email}
                placeholder="your.email@example.com"
                autoComplete="email"
                {...register('email', validationRules.email)}
              />
            </FormControl>
            <FormFieldErrorMessage error={touchedFields.email && errors.email?.message} />
          </FormItem>
        </FormField>
        {/* Email Signup Checkbox */}
        <FormField name="signUpForEmails">
          <FormItem>
            <Controller
              name="signUpForEmails"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={watch('signUpForTexts')}
                    label="Sign up for COIT emails"
                  />
                  <FormDescription className="">
                    I authorize COIT Cleaning &amp; Restoration to send me email updates, special offers, and helpful service information. I understand I can unsubscribe at any time. For more information, please review our <a href="/privacy-policy" target="_blank">Privacy Policy</a>
                  </FormDescription>
                </div>
              )}
            />
          </FormItem>
        </FormField>

        {/* Phone Number */}
        <FormField name="phoneNumber">
          <FormItem>
            <FormLabel required>Phone Number</FormLabel>
            <FormControl>
              <Controller
                name="phoneNumber"
                control={control}
                rules={validationRules.phoneNumber}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="tel"
                    error={touchedFields.phoneNumber && errors.phoneNumber}
                    placeholder="(555) 123-4567"
                    autoComplete="tel"
                    onChange={(e) => {
                      field.onChange(formatPhoneNumber(e.target.value));
                    }}
                  />
                )}
              />
            </FormControl>
            <FormFieldErrorMessage error={touchedFields.phoneNumber && errors.phoneNumber?.message} />
          </FormItem>
        </FormField>
        {/* SMS Signup Checkbox */}
        <FormField name="signUpForTexts">
          <FormItem>
            <Controller
              name="signUpForTexts"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      // Subscribing to texts also opts the user into emails.
                      if (e.target.checked) {
                        setValue('signUpForEmails', true, { shouldValidate: true });
                      }
                    }}
                    label="Get COIT special offers by text"
                  />
                  <FormDescription>
                    I agree to receive recurring SMS marketing messages from COIT Cleaning & Restoration related to special offers and promotions. Message frequency may vary. Message and data rates may apply. I understand I can reply "STOP" to opt out at any time. For more information on how we handle your data, please review our <a href="/privacy-policy" target="_blank">Privacy Policy</a>
                  </FormDescription>
                </div>
              )}
            />
          </FormItem>
        </FormField>
      </Form>

      {/* Address Validation Modal */}
      {showAddressModal && currentAddressData && recommendedAddressData && (
        <AddressValidationModal
          currentAddress={currentAddressData}
          recommendedAddress={recommendedAddressData}
          onSelectAddress={handleAddressSelection}
          onClose={() => setShowAddressModal(false)}
        />
      )}
    </FormStepLayout>
  );
}
